/**
 * tracking-map.js
 */

import React from 'react';
import ReactDOM from 'react-dom/client';
import {
  useTheme,
  Box,
} from '@mui/material';
import { AppMessagingContext } from 'contexts/app-messaging/app-messaging-context';
import { LiveTrackingContext } from '../live-tracking/live-tracking-context';
import { TrackingDrawerContext } from '../input-drawers/drawer-context';
import { headerHeight } from '../input-drawers/control-common';
import { gmapCoords } from './map-common';
import { MapContext } from './map-context';
import Map from './map';
import NoHeadingImg from 'assets/images/no-heading.png';
import HeadingImg from 'assets/images/heading.png';


const arrowTransition = '200ms linear';


const CurrentLocationMarker = (props) => {
  const { theme, appInfo } = props;
  const isAndroid = (appInfo?.platform?.toLowerCase() ?? '').includes('android');
  

  return (
    <Box sx={{
      position: 'relative',
      borderRadius: '50%',
      backgroundColor: '#444',
      height: 24,
      width: 24,
      transform: `translateY(12px)`,
      boxShadow: `
        0px 3px 1px -2px hsla(210, 24%, 16%, 0.6),
        0px 2px 2px 0px hsla(210, 24%, 16%, 0.55),
        0px 1px 8px 0px hsla(210, 24%, 16%, 0.5)
      `,
      display: 'flex',
      padding: '2px',
    }}>
      {/* heading arrow */}
      {!isAndroid &&
      <Box 
        id='current-location-arrow-box' 
        sx={{
          position: 'absolute',
          top: 0,
          bottom: 0,
          left: 0,
          right: 0,
          '& img': {
            transform: 'translate(-88px, -97px)',
            position: 'relative',
            zIndex: 0,
          }
        }} 
        style={{
          transition: arrowTransition,
          transform: `rotate(0deg)`,
        }}
      >
        <img src={NoHeadingImg} alt='no-heading-img' className='no-heading' style={{ position: 'relative', top: '8px' }} />
        <img src={HeadingImg} alt='heading-img' className='heading' style={{ display: 'none' }} />
      </Box>}

      {/* inner circle */}
      <Box sx={{
        flex: 1,
        backgroundColor: theme.palette.comp.tracking.currentLocationColor,
        border: `2px solid white`,
        borderRadius: '50%',
        zIndex: 1,
      }} />
    </Box>
  );
}


export default function TrackingMap(props) {
  const theme = useTheme();
  const { appInfo } = React.useContext(AppMessagingContext);
  const { 
    currentLocation, 
    currentHeading,
    isUserCentered, 
    setIsUserCentered, 
  } = React.useContext(LiveTrackingContext);
  const { mapApi, map } = React.useContext(MapContext);
  const { drawerExpansionHeight } = React.useContext(TrackingDrawerContext);
  const locationMarkerRef = React.useRef();
  const arrowBoxRef = React.useRef();


  // init
  React.useEffect(() => {
    if (theme && mapApi && map) {
      // register event handlers
      const dragListener = map.addListener('dragstart', () => {
        setIsUserCentered(false);
      });

      // create current location marker
      const markerComponent = document.createElement('div');
      const root = ReactDOM.createRoot(markerComponent);
      root.render(<CurrentLocationMarker theme={theme} appInfo={appInfo} />);
      locationMarkerRef.current = new mapApi.Marker({
        map,
        title: 'Current Location',
        content: markerComponent,
        zIndex: 99,
      });

      return () => {
        mapApi.Event.removeListener(dragListener);
        locationMarkerRef.current.map = null;
        arrowBoxRef.current = null;
      }
    }    
  }, [theme, appInfo, mapApi, map, setIsUserCentered]);


  // handle updates to map center
  React.useEffect(() => {
    const center = isUserCentered ? currentLocation : null;
    if (map && center) {
      if (!map.center) {
        map.setCenter(gmapCoords(center));
        map.setZoom(20);
      } else {
        map.panTo(gmapCoords(center));
      }
    }
  }, [map, isUserCentered, currentLocation]);


  // handle updates to map current location
  React.useEffect(() => {
    if (map && locationMarkerRef.current && currentLocation) {
      locationMarkerRef.current.map = map;
      locationMarkerRef.current.position = gmapCoords(currentLocation);
    }
  }, [map, currentLocation]);


  // handle updates to compass heading
  React.useEffect(() => {
    if (!arrowBoxRef.current && currentHeading !== null) {
      const ref = document.querySelector('#current-location-arrow-box');
      if (ref) {
        arrowBoxRef.current = ref;
        arrowBoxRef.current.querySelector('.no-heading').style.display = 'none';
        arrowBoxRef.current.querySelector('.heading').style.display = 'block';
      } else {
        return;
      }
    }

    if (currentHeading !== null) {
      const prevHeading = +((arrowBoxRef.current.style.transform || '0').match(/\d+/)[0]);
      const headingDiff = prevHeading - currentHeading;
      if (headingDiff > 180) {
        arrowBoxRef.current.style.transition = '';
        arrowBoxRef.current.style.transform = `rotate(${prevHeading - 360}deg)`;
        setTimeout(() => {
          arrowBoxRef.current.style.transition = arrowTransition;
          arrowBoxRef.current.style.transform = `rotate(${currentHeading}deg)`;
        }, 50);
      } else if (headingDiff < -180) {
        arrowBoxRef.current.style.transition = '';
        arrowBoxRef.current.style.transform = `rotate(${prevHeading + 360}deg)`;
        setTimeout(() => {
          arrowBoxRef.current.style.transition = arrowTransition;
          arrowBoxRef.current.style.transform = `rotate(${currentHeading}deg)`;
        }, 50);
      } else {
        arrowBoxRef.current.style.transform = `rotate(${currentHeading}deg)`;
      }
    }
  }, [currentHeading]);


  return (
    <>
      <Box sx={{ height: 1 }}>
        <Box sx={{ 
          height: `calc(100vh - ${drawerExpansionHeight + headerHeight}px)`, 
          transition: (theme) => `${theme.transitions.duration.standard}ms ${theme.transitions.easing.easeInOut}`,
        }}>
          <Map />
        </Box>
      </Box>
    </>
  );
}